import React, { useState, useEffect, useCallback } from "react";
import {
  getAnnouncementBar,
  upsertAnnouncementBar,
} from "../../../services/announcementBarService";
import { validateAnnouncementBarSettings } from "../../../utils/validation";
import { toast } from "react-toastify";
import { handleErrors } from "../../../utils/errorHandler";
import ComponentHeader from "../../Common/OtherElements/ComponentHeader";
import { Loading } from "../../Common/OtherElements/Loading";
import { usePageLevelAccess } from "../../../hooks/usePageLevelAccess";
import { useNavigate } from "react-router-dom";

const INITIAL_FORM_STATE = {
  announcementText: "",
  redirectUrl: "",
};

const INITIAL_ERRORS_STATE = {
  announcementText: "",
  redirectUrl: "",
};

const mapAnnouncementBarResponse = (data) => ({
  announcementText:
    data.announcementText || data.AnnouncementText || "",
  redirectUrl: data.redirectUrl || data.RedirectUrl || "",
});

const hasAnnouncementData = (data) => {
  if (!data || typeof data !== "object") return false;
  const text = data.announcementText || data.AnnouncementText || "";
  return Boolean(String(text).trim());
};

export const AnnouncementBarSettingsContent = () => {
  const [pageAccessDetails, setPageAccessDetails] = useState({});
  const pageLevelAccessUrl = "announcement-bar-settings";
  const navigate = useNavigate();
  const { pageAccessData } = usePageLevelAccess(pageLevelAccessUrl);

  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [errors, setErrors] = useState(INITIAL_ERRORS_STATE);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isUpdateMode, setIsUpdateMode] = useState(false);

  useEffect(() => {
    if (pageAccessData) {
      if (!pageAccessData.viewAccess) {
        navigate("/404-error-page");
      } else {
        setPageAccessDetails(pageAccessData);
      }
    }
  }, [pageAccessData, navigate]);

  const fetchAnnouncementBar = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAnnouncementBar();
      if (hasAnnouncementData(data)) {
        setFormData(mapAnnouncementBarResponse(data));
        setIsUpdateMode(true);
      } else {
        setFormData(INITIAL_FORM_STATE);
        setIsUpdateMode(false);
      }
    } catch (error) {
      if (error.response?.status === 404) {
        setFormData(INITIAL_FORM_STATE);
        setIsUpdateMode(false);
      } else {
        handleErrors(error);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (pageAccessDetails.viewAccess) {
      fetchAnnouncementBar();
    }
  }, [pageAccessDetails.viewAccess, fetchAnnouncementBar]);

  const canSubmit =
    isUpdateMode
      ? pageAccessDetails.editAccess
      : pageAccessDetails.addAccess;

  const handleInputChange = ({ target: { name, value } }) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!canSubmit) {
      toast.error("You do not have permission to save this setting.");
      return;
    }

    const { valid, errors: validationErrors } =
      validateAnnouncementBarSettings(formData);
    setErrors(validationErrors);

    if (!valid) return;

    const payload = {
      announcementText: formData.announcementText.trim(),
      redirectUrl: formData.redirectUrl.trim(),
    };

    try {
      setIsButtonDisabled(true);
      const response = await upsertAnnouncementBar(payload);

      if (response?.data?.isSuccess === false) {
        toast.error(
          response.data.message || "Request failed. Please try again."
        );
        return;
      }

      toast.success(
        response.data?.message ||
          `Announcement bar ${isUpdateMode ? "updated" : "saved"} successfully!`
      );
      await fetchAnnouncementBar();
    } catch (error) {
      handleErrors(error);
    } finally {
      setIsButtonDisabled(false);
    }
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <>
      <ComponentHeader title="Announcement Bar Settings" />

      <div className="row justify-content-center">
        <div className="col-xxl-12 col-lg-12 col-12">
          <div className="card mt-xxl-n5">
            <div className="card-header">
              <h5 className="mb-sm-1 mt-sm-1">Announcement Bar Settings</h5>
            </div>

            <div className="card-body p-4">
              <form onSubmit={handleSubmit} method="POST">
                <div className="row">
                  <div className="col-12">
                    <div className="mb-3">
                      <label htmlFor="announcementText" className="form-label">
                        Announcement Text{" "}
                        <span className="required-field">*</span>
                      </label>
                      <textarea
                        name="announcementText"
                        id="announcementText"
                        rows="4"
                        placeholder="Enter announcement text"
                        value={formData.announcementText}
                        onChange={handleInputChange}
                        className={`form-control ${
                          errors.announcementText ? "is-invalid" : ""
                        }`}
                      />
                      {errors.announcementText && (
                        <div className="invalid-feedback">
                          {errors.announcementText}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-12">
                    <div className="mb-3">
                      <label htmlFor="redirectUrl" className="form-label">
                        Redirect URL
                      </label>
                      <input
                        type="url"
                        name="redirectUrl"
                        id="redirectUrl"
                        placeholder="https://example.com"
                        value={formData.redirectUrl}
                        onChange={handleInputChange}
                        className={`form-control ${
                          errors.redirectUrl ? "is-invalid" : ""
                        }`}
                      />
                      {errors.redirectUrl && (
                        <div className="invalid-feedback">
                          {errors.redirectUrl}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-12">
                    {canSubmit ? (
                      <button
                        type="submit"
                        className="btn btn-secondary pt-1 pb-1 p-3"
                        disabled={isButtonDisabled}
                      >
                        {isButtonDisabled
                          ? isUpdateMode
                            ? "Updating..."
                            : "Saving..."
                          : isUpdateMode
                          ? "Update Announcement Bar"
                          : "Save Announcement Bar"}
                      </button>
                    ) : (
                      <p className="text-danger mb-0">
                        You do not have permission to save changes.
                      </p>
                    )}
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
